import BlankItem from '@components/blank/BlankItem';
import Header from '@components/Header';
import Loading from '@components/Loading';
import styled from '@emotion/styled';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CategoryTabs from './components/CategoryTabs';
import ImageGrid from './components/ImageGrid';
import usePrepaidCardImages from './hooks/usePrepaidCardImages';

interface Params {
    type: 'GIFT' | 'BUY';
    giftItemId?: string;
}

declare module '@stackflow/config' {
    interface Register {
        PrepaidCardImagesActivity: Params;
    }
}

const PrepaidCardImagesActivity: ActivityComponentType<'PrepaidCardImagesActivity'> = ({ params }) => {
    const { t } = useTranslation('prepaidCard');
    const { type, giftItemId } = params;
    const { images: categories, isLoading, isBlank } = usePrepaidCardImages();
    const [selectedCategory, setSelectedCategory] = useState('');

    useEffect(() => {
        if (!categories?.length) return;
        const exists = categories.some((c) => c.categoryName === selectedCategory);
        if (!exists) {
            setSelectedCategory(categories[0].categoryName);
        }
    }, [categories, selectedCategory]);

    const selected = categories?.find((c) => c.categoryName === selectedCategory);
    const categoryNames = categories?.map((c) => c.categoryName) ?? [];
    const isCategoryVisible = categoryNames.length > 1;

    return (
        <AppScreen>
            <Header title={t(type === 'GIFT' ? 'images.giftTitle' : 'images.title')} />
            {isLoading && <Loading size='small' />}
            {!isLoading && !isBlank && (
                <>
                    {isCategoryVisible && (
                        <CategoryTabs
                            categories={categoryNames}
                            selected={selectedCategory}
                            onSelect={setSelectedCategory}
                        />
                    )}
                    {selected && (
                        <GridWrapper isCategoryVisible={isCategoryVisible}>
                            <ImageGrid
                                images={selected.images}
                                type={type}
                                giftItemId={giftItemId}
                            />
                        </GridWrapper>
                    )}
                </>
            )}
            {isBlank && <BlankItem />}
        </AppScreen>
    );
};

const GridWrapper = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isCategoryVisible',
})<{
    isCategoryVisible: boolean;
}>`
    width: 100%;
    padding: ${({ isCategoryVisible }) => (isCategoryVisible ? 0 : 'var(--spacing-6) 0')};
`;

export default PrepaidCardImagesActivity;
